import { syncMe } from '@shared/api/accounts';
import { ApiError } from '@shared/api/client';
import {
  auth0Service,
  SESSION_EXPIRED_EVENT,
  type BootstrapAppState,
} from '@shared/lib/auth0-service';
import { navigate } from '@shared/router/navigation';
import type { AccountRole } from '@shared/types/account';
import type { SessionUser } from '@shared/types/session';

import { pickAppRole, pickOrgId } from './claims';
import { isReauthRequiredError } from './reauth';

/// JIT 同期 (`/api/me/sync`) の進行状態。
/// - `unprovisioned`: 401 = ログイン中の email がまだ担当者(app_accounts)として招待されていない。
///   認証/認可は Auth0 (org メンバーシップ + JWT) で完結しているので**アプリ起動は妨げない**。
///   将来の「未招待です」UI (Discussion #238) で拾う。
/// - `error`: それ以外の一時的失敗。
type SyncStatus = 'idle' | 'syncing' | 'synced' | 'unprovisioned' | 'error';

interface SessionStoreState {
  user: SessionUser | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  /// JIT 同期で解決した担当者 (app_accounts) の不変 ID。owner 選択 (ACC-3 以降) で使う。
  accountId: string | null;
  /// `app_account_current_role` View 由来の現在 role (表示用、認可には使わない)。
  currentRole: AccountRole | null;
  syncStatus: SyncStatus;
}

const state = $state<SessionStoreState>({
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  accountId: null,
  currentRole: null,
  syncStatus: 'idle',
});

/// 再ログイン後に復元する「今いるパス」 を組み立てる。 pathname だけでなく
/// search / hash も含めることで、 クエリ付き一覧や hash アンカーまで戻せる。
function buildReturnTo(): string {
  return window.location.pathname + window.location.search + window.location.hash;
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

async function redirectToLogin(): Promise<never> {
  await auth0Service.loginWithRedirect({
    appState: { returnTo: buildReturnTo() } satisfies BootstrapAppState,
  });
  // loginWithRedirect は内部で location 遷移を仕掛ける。 直後に SPA を mount
  // させないため、 解決しない Promise を返して mount を抑止する。
  return new Promise<never>(() => undefined);
}

async function syncUserFromAuth0(): Promise<void> {
  const auth0User = await auth0Service.getUser();
  if (!auth0User?.sub) {
    state.user = null;
    return;
  }
  state.user = {
    id: auth0User.sub,
    name: auth0User.name ?? auth0User.nickname ?? auth0User.email ?? auth0User.sub,
    email: auth0User.email ?? '',
    role: pickAppRole(auth0User),
    orgId: pickOrgId(auth0User),
  };
}

export const sessionStore = {
  get user() {
    return state.user;
  },
  get isAuthenticated() {
    return state.isAuthenticated;
  },
  get loading() {
    return state.loading;
  },
  get error() {
    return state.error;
  },
  get accountId() {
    return state.accountId;
  },
  get currentRole() {
    return state.currentRole;
  },
  get syncStatus() {
    return state.syncStatus;
  },

  /// SPA 起動時に 1 回呼ぶ。
  /// 1. URL に `?invitation=&organization=` が乗っていれば Auth0 Organizations の
  ///    招待リンクから着地した状態。 これらを `loginWithRedirect` の
  ///    `authorizationParams` に透過して招待承諾フローを開始する。
  /// 2. URL に `?code=&state=` が乗っていれば redirect 後のコールバックを消化し、
  ///    クエリパラメータを history から除去する。
  /// 3. 認証済みなら Auth0 から user 情報を取得して state に格納する。
  /// 4. 未認証なら `loginWithRedirect` で Auth0 ログイン画面に飛ばし、
  ///    呼び出し元が SPA を mount しないように永遠に pending な Promise を返す。
  async initialize(): Promise<void> {
    state.loading = true;
    state.error = null;
    try {
      const url = new URL(window.location.href);

      // Auth0 Organizations の招待リンク経由で着地したケース。
      // 招待メールから来た URL は `<initiateLoginUri>?invitation=...&organization=...`
      // (+ optional `organization_name`) の形式で、 これらを `authorizationParams`
      // に透過しないと Auth0 は通常ログインとして処理してしまい、 招待された
      // ユーザーは `organizationUsage='require'` に阻まれて `client requires
      // organization membership, but user does not belong to any organization`
      // で redirect loop に陥る。 仕様:
      // https://auth0.com/docs/manage-users/organizations/configure-organizations/invite-members
      //
      // 招待を消化したあとはコールバックで `?code=&state=` 付きで戻ってくるため、
      // 上記 2. の redirect callback 分岐に合流する。 `loginWithRedirect` 内部で
      // location 遷移が走るので、 既存の未認証分岐と同様に永遠 Promise で
      // SPA mount を抑止する。
      const invitation = url.searchParams.get('invitation');
      const organization = url.searchParams.get('organization');
      if (invitation && organization) {
        await auth0Service.loginWithRedirect({
          authorizationParams: { invitation, organization },
          appState: { returnTo: '/' } satisfies BootstrapAppState,
        });
        await new Promise<never>(() => undefined);
      }

      if (url.searchParams.has('code') && url.searchParams.has('state')) {
        const { appState } = await auth0Service.handleRedirectCallback();
        window.history.replaceState({}, document.title, url.pathname + url.hash);
        // deep-link 復元: ログイン開始時のパス (appState.returnTo) へ戻す。
        // redirect_uri は origin なのでコールバック直後のパスは常に "/" になる。
        // この initialize() は main.ts で startRouter() より前に await されるため、
        // navigate() の pushState が startRouter() の初回ルート解決に反映される
        // (NAVIGATE_EVENT 自体は購読前で失われるが pushState で URL は確定済み)。
        if (appState?.returnTo && appState.returnTo !== '/') {
          navigate(appState.returnTo);
        }
      }

      const authenticated = await auth0Service.isAuthenticated();
      state.isAuthenticated = authenticated;
      if (authenticated) {
        await syncUserFromAuth0();
        return;
      }

      await redirectToLogin();
    } catch (err) {
      if (isReauthRequiredError(err)) {
        try {
          await redirectToLogin();
        } catch (redirectErr) {
          state.error = errorMessage(redirectErr);
          return;
        }
      }
      state.error = errorMessage(err);
    } finally {
      state.loading = false;
    }
  },

  /// ログイン確立後に 1 回呼ぶ JIT 同期 (ADR §7 / ACC-2)。`/api/me/sync` を叩いて
  /// identity を populate し、解決した `account_id` / `current_role` を保持する。
  /// 発火配線は app 層 (`bootstrapAuth`)、フロー判断はここ (features) が持つ。
  ///
  /// **JIT 同期は担当者(assignee)履歴のための処理であって、認証/認可のゲートではない**
  /// (認可は Auth0 org メンバーシップ + JWT claim で完結、CLAUDE.md / ADR §5)。よって
  /// 同期が失敗してもアプリ起動は妨げず、`syncStatus` に記録するだけに留める:
  /// - **401 (未招待)**: `syncStatus='unprovisioned'`。ここでログアウトすると Auth0 の SSO
  ///   再ログインと衝突して redirect ループになるため、強制ログアウトはしない。
  /// - **その他 (一時的失敗)**: `syncStatus='error'` + error メッセージ。
  async sync(): Promise<void> {
    state.syncStatus = 'syncing';
    try {
      const result = await syncMe();
      state.accountId = result.account_id;
      state.currentRole = result.current_role;
      state.syncStatus = 'synced';
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        state.syncStatus = 'unprovisioned';
        return;
      }
      state.error = errorMessage(err);
      state.syncStatus = 'error';
    }
  },

  async logout(): Promise<void> {
    state.error = null;
    try {
      await auth0Service.logout();
      state.user = null;
      state.isAuthenticated = false;
      state.accountId = null;
      state.currentRole = null;
      state.syncStatus = 'idle';
    } catch (err) {
      state.error = errorMessage(err);
      throw err;
    }
  },
};

/// access token の透過更新に失敗したとき (refresh token 失効等)、 今いるパスを
/// `returnTo` に積んで Auth0 ログインへリダイレクトする。 callback で
/// `appState.returnTo` を読んで元の画面へ復帰する (initialize 参照)。
async function reauthenticate(): Promise<void> {
  try {
    await redirectToLogin();
  } catch (err) {
    state.error = errorMessage(err);
  }
}

let sessionWatcherStarted = false;

/// `SESSION_EXPIRED_EVENT` (shared/api/client が発火) を購読し、 再ログインへ
/// 繋ぐ。 配線は app 層 (bootstrapAuth) から 1 回だけ呼ぶ想定で、 `startRouter()`
/// と同様に多重登録ガードを持つ。
export function startSessionWatcher(): void {
  if (sessionWatcherStarted || typeof window === 'undefined') return;
  window.addEventListener(SESSION_EXPIRED_EVENT, () => {
    void reauthenticate();
  });
  sessionWatcherStarted = true;
}
